import { useMemo } from 'react'
import type { LedgerEntry, LedgerEnvelope } from './models'
import { useFetchLedger } from './useFetchLedger'

type UseFetchPayoutsLedgerArgs = {
  page: number
  limit: number
}

export type PayoutsLedgerResult = {
  data: LedgerEntry[]
  meta: { hasMore: boolean }
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
}

const envelopeHasMore = (envelope: LedgerEnvelope | undefined): boolean => {
  if (!envelope) return false
  return envelope.meta.total > envelope.meta.page * envelope.meta.limit
}

export const useFetchPayoutsLedger = ({ page, limit }: UseFetchPayoutsLedgerArgs): PayoutsLedgerResult => {
  const debits = useFetchLedger({ kind: 'PAYOUT_DEBIT', page, limit })
  const refunds = useFetchLedger({ kind: 'PAYOUT_REFUND', page, limit })

  const data = useMemo(() => {
    const debitRows = debits.data?.data ?? []
    const refundRows = refunds.data?.data ?? []
    return [...debitRows, ...refundRows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [debits.data, refunds.data])

  const hasMore = envelopeHasMore(debits.data) || envelopeHasMore(refunds.data)

  return {
    data,
    meta: { hasMore },
    isLoading: debits.isLoading || refunds.isLoading,
    isError: debits.isError || refunds.isError,
    isFetching: debits.isFetching || refunds.isFetching,
    refetch: () => {
      debits.refetch()
      refunds.refetch()
    }
  }
}
